/**
 * TECH-JOB-SCREEN §8 item 2 — the signal-loss save outbox (v1).
 *
 * Every workflow save writes to the local outbox BEFORE the network
 * attempt, so entered data is UNLOSABLE from the moment the tech taps
 * Save:
 *
 *   queue → try the network → success? clear the queued row
 *                           → network error / timeout / 5xx? keep it —
 *                             the existing drain (online event, focus,
 *                             Background Sync) retries with backoff
 *                           → semantic 4xx? rethrow — a validation
 *                             rejection can never succeed by retrying, the
 *                             tech must see the server's plain message. A
 *                             row this tap created is cleared (the form is
 *                             still open with the readings). A row that was
 *                             ALREADY waiting (an earlier tap, often with no
 *                             signal) is parked in "Failed saves" with that
 *                             message instead — never deleted.
 *
 * The server's test save is an idempotent upsert on (job_id, asset_id),
 * so a timed-out request that actually landed is safe to replay.
 */
import { ApiError, getToken } from "./api";
import { getVerifiedOnline, probeConnectivityNow } from "./connectivity";
import { plainRejectionMessage } from "./offline-replay";
import { parkQueuedAction, queueOfflineActionDetailed, removeQueuedAction } from "./offline-sync";
import { recordQueueEvent, type QueueReason } from "./queue-telemetry";

/** Weak-signal ceilings: a save that can't finish in this window goes to
 *  the outbox instead of spinning forever in a mechanical room. */
const SAVE_TIMEOUT_MS = 20_000;

export interface OutboxSaveAction {
    url: string;
    method: string;
    body: unknown;
}

export type OutboxSaveResult<T> = { queued: true } | { queued: false; res: T };

/** A 4xx (other than 408/429) is a semantic rejection — replaying the
 *  identical payload cannot succeed, so it must NOT sit in the outbox. */
export function isPermanentSaveRejection(err: unknown): boolean {
    return err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 408 && err.status !== 429;
}

/** Why a failed network attempt is being queued — for #693 telemetry. A
 *  timeout (`AbortSignal.timeout` → DOMException "TimeoutError", or "AbortError"
 *  on older engines) and a 5xx are distinguished from a plain network error so
 *  a server blip is never miscounted as "offline". */
function classifyQueueFailure(err: unknown): QueueReason {
    if (err instanceof DOMException && (err.name === "TimeoutError" || err.name === "AbortError")) {
        return "write_timeout";
    }
    if (err instanceof ApiError && err.status >= 500) return "write_http_5xx";
    return "write_network_error";
}

/** Best-effort job id for telemetry clustering — the outbox body carries it on
 *  most field saves. */
function jobIdOf(action: OutboxSaveAction): string | null {
    const body = action.body as { jobId?: unknown } | null;
    return body && typeof body === "object" && typeof body.jobId === "string" ? body.jobId : null;
}

/** The server's own `message`, when its error body carries one. */
function serverMessageOf(err: ApiError): string | null {
    const body = err.body as { message?: unknown } | null;
    return body && typeof body === "object" && typeof body.message === "string" && body.message.trim()
        ? body.message.trim()
        : null;
}

async function attemptNetwork<T>(action: OutboxSaveAction, timeoutMs: number): Promise<T> {
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
    };
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(action.url, {
        method: action.method,
        headers,
        body: JSON.stringify(action.body),
        signal: AbortSignal.timeout(timeoutMs),
    });
    const text = await res.text();
    let body: unknown = text;
    try {
        body = JSON.parse(text);
    } catch {
        /* raw text */
    }
    if (!res.ok) throw new ApiError(res.status, res.statusText, body);
    return body as T;
}

export async function saveThroughOutbox<T>(
    action: OutboxSaveAction,
    opts: { timeoutMs?: number } = {},
): Promise<OutboxSaveResult<T>> {
    // 1. The outbox row exists BEFORE any network is attempted.
    const { id: queuedId, reused } = await queueOfflineActionDetailed(action);

    // 2. Confirmed-offline: don't even try — the drain sends it on reconnect.
    //    This reads the reachability-VERIFIED state, not the raw
    //    `navigator.onLine` flag; the flag's false-negatives (full signal,
    //    still reported offline) used to skip the network here and strand the
    //    save in the queue. See `connectivity.ts`.
    if (!getVerifiedOnline()) {
        recordQueueEvent("offline_confirmed", { url: action.url, method: action.method, jobId: jobIdOf(action) });
        return { queued: true };
    }

    // 3. Try the network.
    try {
        const res = await attemptNetwork<T>(action, opts.timeoutMs ?? SAVE_TIMEOUT_MS);
        await removeQueuedAction(queuedId);
        return { queued: false, res };
    } catch (err) {
        if (isPermanentSaveRejection(err)) {
            if (reused) {
                // Martin High School (10/27): this tap folded into a save that
                // was already queued. Deleting that row threw away the only
                // queued copy of the readings and left the tech with a bare
                // "Save failed (409)". Park it with the server's words instead.
                const apiErr = err as ApiError;
                await parkQueuedAction(queuedId, {
                    status: apiErr.status,
                    message: serverMessageOf(apiErr) ?? plainRejectionMessage(apiErr.status),
                    terminal: true,
                });
            } else {
                await removeQueuedAction(queuedId);
            }
            throw err;
        }
        // Network error / timeout / 5xx — the row stays queued and the caller
        // reports "waiting for signal" instead of a dead save. Record WHY it
        // queued (#693), then, since a real network failure is a strong offline
        // signal, re-confirm connectivity now: that updates the banner and the
        // gate for the next save without waiting for the browser's (unreliable)
        // offline event.
        recordQueueEvent(classifyQueueFailure(err), {
            url: action.url,
            method: action.method,
            jobId: jobIdOf(action),
        });
        void probeConnectivityNow();
        return { queued: true };
    }
}
